import { clearPostAuthAnimationFlag } from "src/utils/auth";
import { getTierByPlanId } from "src/utils/plans";
import { SubscriptionStatusData } from "src/utils/api/subscriptionStatus";
import { routeConstants } from "src/utils/constants";
import {
  noomMedOfferLayer,
  paymentCheckoutLayer,
} from "src/pageDefinitions/goto/constants";
import goto from "src/pageDefinitions/goto";
import { trackEvent } from "src/utils/api/tracker";
import { getActiveProgram } from "src/utils/api/userDetails";
import { captureException } from "src/utils/error";
import {
  getSessionState,
  updateSessionState,
} from "src/pageDefinitions/session";

declare module "src/pageDefinitions/session" {
  interface BrowserSessionState {
    isPostAuthCheckoutSession?: boolean;
  }
}

export function isPostAuthCheckoutSession(): boolean {
  return getSessionState("browser").isPostAuthCheckoutSession === true;
}

export type UserType = "noomWeight" | "noomMed" | "freemium" | "noSubscription";

/** Must match {@link startMeasuring} in post-auth flow (see PostAuthAnimation). */
export const POST_AUTH_ACCOUNT_CHECK_TASK_NAME = "postAuthAccountCheck";

const productSwitchLayer = "product-switch";

async function gotoAfterPostAuth(userType: UserType) {
  updateSessionState("browser", { isPostAuthCheckoutSession: true });
  switch (userType) {
    case "freemium":
      await goto.layer(
        paymentCheckoutLayer,
        null,
        true,
        routeConstants.freemiumClinical,
        undefined
      );
      break;
    case "noSubscription":
      await goto.layer(
        paymentCheckoutLayer,
        null,
        true,
        routeConstants.clinical,
        undefined
      );
      break;
    case "noomWeight":
      await goto.layer(
        noomMedOfferLayer,
        null,
        true,
        undefined,
        "medUpgradePayment"
      );
      break;
    case "noomMed":
      await goto.layer(productSwitchLayer, null, true);
      break;
    default:
      break;
  }
}

export async function getUserType(
  accessCode?: string,
  subscriptionStatus?: SubscriptionStatusData
): Promise<UserType> {
  if (!accessCode) {
    return "noSubscription";
  }

  const activeProgram = await getActiveProgram(accessCode);
  if (!activeProgram) {
    return "noSubscription";
  }

  const slug = activeProgram.programConfigurationSlug ?? "";
  if (slug.startsWith("FREE")) {
    return "freemium";
  }

  if (!subscriptionStatus?.hasActiveSubscription) {
    return "noSubscription";
  }

  const tier = getTierByPlanId(subscriptionStatus.planId);
  if (tier === "MED") {
    return "noomMed";
  }

  return "noomWeight";
}

export async function navigateAfterAnimation(
  userType: UserType,
  latencyMs: number | undefined
): Promise<void> {
  clearPostAuthAnimationFlag();

  trackEvent("PostAuthAccountCheckCompleted", {
    userType,
    latencyMs,
  });

  trackEvent("PostAuthUserTypeIdentified", {
    userType,
  });

  if (userType === "noomWeight") {
    trackEvent("PostAuthUpgradeEligibilityDetermined", {
      userType,
      eligible: true,
    });

    trackEvent("PostAuthUpgradeFlowInitiated", {
      userType,
    });
  } else {
    trackEvent("PostAuthRoutedToCheckout", {
      userType,
    });
  }

  try {
    await gotoAfterPostAuth(userType);
  } catch (err) {
    captureException(err, "PostAuthNavigateAfterAnimation");
  }
}
